(function qualucaBmiCalculator() {
  'use strict';

  const core = window.QualucaBmiCore;
  const tracking = window.QualucaBmiTrackingCore;
  const CONSENT_VERSION = '2026-08-25-v3';
  const REQUEST_CONTRACT_VERSION = '2026-08-27-sex-v1';
  if (!core || !tracking) return;

  const numberFormatter = new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const measurementFormatter = new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  function formatDecimal(value) {
    return numberFormatter.format(value);
  }

  function track(name, privacyState) {
    if (!privacyState?.isAnalyticsAllowed()) return;
    document.dispatchEvent(new CustomEvent('qualuca:analytics', {
      detail: {event: name},
    }));

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, {});
    }
  }

  function readPrograms(root) {
    const config = root.querySelector('[data-qbmi-programs]');
    if (!config) return [];

    try {
      return JSON.parse(config.textContent).filter((program) => program && program.durationDays);
    } catch (error) {
      console.warn('[Qualuca IMC] Configurația produselor nu a putut fi citită.', error);
      return [];
    }
  }

  function init(root) {
    if (root.dataset.qbmiReady === 'true') return;
    root.dataset.qbmiReady = 'true';

    const form = root.querySelector('[data-qbmi-form]');
    const sexField = root.querySelector('[data-qbmi-sex-field]');
    const sexInputs = Array.from(root.querySelectorAll('[data-qbmi-sex]'));
    const weightInput = root.querySelector('[data-qbmi-weight]');
    const heightInput = root.querySelector('[data-qbmi-height]');
    const placeholder = root.querySelector('[data-qbmi-placeholder]');
    const result = root.querySelector('[data-qbmi-result]');
    const resultCard = root.querySelector('[data-qbmi-result-card]');
    const recommendation = root.querySelector('[data-qbmi-recommendation]');
    const savePanel = root.querySelector('[data-qbmi-save-panel]');
    const saveForm = root.querySelector('[data-qbmi-save-form]');
    const saveEmail = root.querySelector('[data-qbmi-save-email]');
    const saveConsent = root.querySelector('[data-qbmi-save-consent]');
    const saveButton = root.querySelector('[data-qbmi-save-button]');
    const saveMessage = root.querySelector('[data-qbmi-save-message]');
    const programs = readPrograms(root);
    let recommendedDuration = null;
    let selectedDuration = null;
    let lastMetrics = null;
    let eventToken = '';
    let submissionToken = '';
    let saveInFlight = false;
    let calculationPromise = null;
    let identified = false;
    let inFlightSignature = '';
    let lastSubmittedSignature = '';
    let lastSubmittedAt = 0;
    const privacyState = tracking.createPrivacyState({window, document});

    if (!form || !sexField || sexInputs.length !== 2 || !weightInput || !heightInput) return;

    function setError(field, message) {
      const error = root.querySelector(`[data-qbmi-error="${field}"]`);
      if (error) error.textContent = message || '';
      if (field === 'sex') {
        sexField.setAttribute('aria-invalid', message ? 'true' : 'false');
        return;
      }
      const input = field === 'weight' ? weightInput : heightInput;
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    function clearErrors() {
      ['sex', 'weight', 'height'].forEach((field) => setError(field, ''));
    }

    function setText(selector, value) {
      const element = root.querySelector(selector);
      if (element) element.textContent = value;
    }

    function createEventToken() {
      return tracking.randomToken(window.crypto);
    }

    async function postCalculator(payload, attempts = 2) {
      let lastError = null;
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
          const response = await fetch(root.dataset.qbmiApi, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            keepalive: payload.action === 'details',
            body: JSON.stringify(payload),
          });
          const body = await response.json().catch(() => ({}));
          if (!response.ok || !body.ok) throw new Error(body.error || 'Cererea nu a putut fi procesată.');
          return body;
        } catch (error) {
          lastError = error;
          if (attempt + 1 < attempts) await new Promise((resolve) => window.setTimeout(resolve, 250));
        }
      }
      throw lastError || new Error('Cererea nu a putut fi procesată.');
    }

    async function persistCalculation(metrics, calculationToken, sex) {
      if (!root.dataset.qbmiApi || !calculationToken) return null;
      const program = programForDuration(metrics.recommendation?.durationDays);
      const privacy = privacyState.snapshot();
      const payload = {
        action: 'calculate',
        requestContractVersion: REQUEST_CONTRACT_VERSION,
        calculationEventId: calculationToken,
        sex,
        weightKg: metrics.weightKg,
        heightCm: metrics.heightCm,
        recommendedDuration: metrics.recommendation?.durationDays || null,
      };
      if (privacy.analyticsAllowed) {
        Object.assign(payload, privacy, {
          recommendedProductId: program?.productId || null,
          recommendedProductHandle: program?.productHandle || null,
        });
      }
      return postCalculator(payload);
    }

    function setSaveMessage(message, success) {
      if (!saveMessage) return;
      saveMessage.textContent = message || '';
      saveMessage.classList.toggle('is-success', Boolean(success));
    }

    function programForDuration(duration) {
      return programs.find((item) => Number(item.durationDays) === Number(duration)) || null;
    }

    async function syncCartAttribution(token, calculationId) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({attributes: {
          'Qualuca - Calculator Attribution': token || '',
          'Qualuca - Calculator ID': '',
        }}),
      });
      if (!response.ok) throw new Error('Asocierea cu o comandă nu a putut fi pregătită.');
      if (token && calculationId && root.dataset.qbmiApi) {
        await postCalculator({action: 'cart', calculationEventId: calculationId}, 1);
      }
    }

    async function saveDetailedResult(event) {
      event.preventDefault();
      if (!lastMetrics || saveInFlight || identified || !saveEmail || !saveConsent || !saveButton) return;

      const email = saveEmail.value.trim();
      if (!email || !saveEmail.checkValidity()) {
        setSaveMessage('Introdu o adresă de e-mail validă.', false);
        saveEmail.focus();
        return;
      }
      if (!saveConsent.checked) {
        setSaveMessage('Bifează acordul pentru a salva rezultatul.', false);
        saveConsent.focus();
        return;
      }
      if (!root.dataset.qbmiApi) {
        setSaveMessage('Salvarea nu este disponibilă momentan. Încearcă din nou mai târziu.', false);
        return;
      }

      saveInFlight = true;
      saveButton.disabled = true;
      saveButton.textContent = 'SE SALVEAZĂ…';
      setSaveMessage('', false);
      const recommendedProgram = programForDuration(lastMetrics.recommendation?.durationDays);

      try {
        if (calculationPromise) await calculationPromise;
        if (!eventToken) throw new Error('Calculul anonim nu a putut fi confirmat. Încearcă din nou.');
        const payload = await postCalculator({
          action: 'identify', consent: true, consentVersion: CONSENT_VERSION,
          calculationEventId: eventToken, email,
          recommendedProductId: recommendedProgram?.productId || null,
          recommendedProductHandle: recommendedProgram?.productHandle || null,
        });
        identified = true;
        if (payload.attributionToken) {
          if (privacyState.isAnalyticsAllowed()) privacyState.setAttribution(payload.attributionToken);
          await syncCartAttribution(payload.attributionToken, eventToken);
        }
        setSaveMessage('Rezultatul a fost salvat cu acordul tău pentru maximum 90 de zile.', true);
        saveButton.textContent = 'REZULTAT SALVAT';
        track('bmi_result_enrolled', privacyState);
      } catch (error) {
        setSaveMessage(error.message || 'Rezultatul nu a putut fi salvat. Încearcă din nou.', false);
        saveButton.disabled = false;
        saveButton.textContent = 'SALVEAZĂ REZULTATUL';
      } finally {
        saveInFlight = false;
      }
    }

    async function recordAttributedCta() {
      if (!selectedDuration || !root.dataset.qbmiApi) return;
      if (calculationPromise) await calculationPromise.catch(() => null);
      if (!eventToken) return;
      const selectedProgram = programForDuration(selectedDuration);
      try {
        const payload = await postCalculator({
          action: 'details', calculationEventId: eventToken, selectedDuration,
          selectedProductId: selectedProgram?.productId || null,
          selectedProductHandle: selectedProgram?.productHandle || null,
        }, 1);
        if (payload.attributionToken) {
          if (privacyState.isAnalyticsAllowed()) privacyState.setAttribution(payload.attributionToken);
          await syncCartAttribution(payload.attributionToken, eventToken);
        }
      } catch (_) {
        // CTA-ul continuă să funcționeze chiar dacă statistica nu poate fi actualizată.
      }
    }

    function renderProduct(duration) {
      const product = programs.find((item) => Number(item.durationDays) === Number(duration));
      const options = root.querySelectorAll('[data-qbmi-program-option]');

      options.forEach((option) => {
        const optionDuration = Number(option.dataset.qbmiProgramOption);
        const isSelected = optionDuration === Number(duration);
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        option.classList.toggle('is-selected', isSelected);
        option.classList.toggle('is-recommended', optionDuration === Number(recommendedDuration));
      });

      if (!product) return;
      selectedDuration = Number(product.durationDays);

      const image = root.querySelector('[data-qbmi-product-image]');
      const compare = root.querySelector('[data-qbmi-product-compare]');
      const cta = root.querySelector('[data-qbmi-product-cta]');
      const badge = root.querySelector('[data-qbmi-recommended-badge]');

      setText('[data-qbmi-product-duration]', `Program ${product.durationDays} zile`);
      setText('[data-qbmi-product-program]', `Complex Qualuca — program ${product.durationDays} zile`);
      setText('[data-qbmi-product-title]', product.title || `Complex Qualuca – ${product.durationDays} zile`);
      setText('[data-qbmi-product-price]', product.price || 'Preț indisponibil');
      setText(`[data-qbmi-program-price="${product.durationDays}"]`, product.price || '');
      setText(
        '[data-qbmi-product-stage]',
        lastMetrics?.recommendation?.isMaintenance && selectedDuration === 30
          ? 'Opțiune orientativă pentru susținerea rutinei de menținere a siluetei.'
          : lastMetrics && lastMetrics.recommendation && lastMetrics.recommendation.isInitialStage && selectedDuration === 90
          ? 'Cea mai potrivită etapă inițială dintre programele disponibile.'
          : 'Program selectat în funcție de durata orientativă calculată.'
      );

      if (image) {
        image.src = product.image || '';
        image.alt = product.imageAlt || product.title || `Complex Qualuca – ${product.durationDays} zile`;
        image.hidden = !product.image;
      }

      if (compare) {
        compare.textContent = product.compareAtPrice || '';
        compare.hidden = !product.compareAtPrice;
      }

      if (badge) badge.hidden = selectedDuration !== Number(recommendedDuration);

      if (cta) {
        cta.textContent = `VEZI PROGRAMUL DE ${product.durationDays} ZILE`;
        const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.setAttribute('viewBox', '0 0 24 24');
        arrow.setAttribute('width', '20');
        arrow.setAttribute('height', '20');
        arrow.innerHTML = '<path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>';
        cta.appendChild(arrow);
        cta.href = product.url || '#';
        cta.setAttribute('aria-disabled', product.available && product.url ? 'false' : 'true');
      }

      setText(
        '[data-qbmi-availability]',
        product.available ? 'Disponibil în magazin' : 'Produs indisponibil momentan'
      );
    }

    function populateProgramPrices() {
      programs.forEach((product) => {
        setText(`[data-qbmi-program-price="${product.durationDays}"]`, product.price || '');
      });
    }

    function renderExplanation(metrics) {
      const min = formatDecimal(metrics.healthyMinKg);
      const max = formatDecimal(metrics.healthyMaxKg);
      if (metrics.recommendation?.isMaintenance) {
        setText(
          '[data-qbmi-explanation]',
          `La înălțimea de ${measurementFormatter.format(metrics.heightCm)} cm, intervalul de greutate asociat unui IMC normal este aproximativ ${min}–${max} kg. Greutatea introdusă se află în acest interval. Dacă dorești să îți susții rutina de menținere a siluetei, poți vedea Complexul Qualuca de 30 de zile.`
        );
        return;
      }

      const above = formatDecimal(metrics.kgAboveHealthyRange);
      const optimistic = core.displayDays(metrics.estimatedDays.optimistic);
      const conservative = core.displayDays(metrics.estimatedDays.conservative);
      const duration = metrics.recommendation.durationDays;
      let text = `La înălțimea de ${measurementFormatter.format(metrics.heightCm)} cm, intervalul de greutate asociat unui IMC normal este aproximativ ${min}–${max} kg. `;

      text += `Greutatea introdusă este cu aproximativ ${above} kg peste limita superioară a acestui interval. `;
      text += `La un ritm orientativ de 2–4 kg la fiecare 30 de zile, durata estimată este de aproximativ ${optimistic}–${conservative} zile. `;

      if (metrics.recommendation.isInitialStage) {
        text += `Pentru alegerea prudentă folosim ritmul de 2 kg la 30 de zile, iar obiectivul complet ar putea necesita aproximativ ${conservative} de zile. Complexul de 90 de zile este cea mai potrivită etapă inițială dintre programele disponibile.`;
      } else {
        text += `Pentru alegerea prudentă folosim ritmul de 2 kg la 30 de zile. Dintre programele disponibile, complexul de ${duration} de zile este cel mai scurt care acoperă această estimare.`;
      }

      setText('[data-qbmi-explanation]', text);
    }

    function renderMetrics(metrics) {
      const category = metrics.category;
      const differenceLabel = root.querySelector('[data-qbmi-difference-label]');
      const durationRow = root.querySelector('[data-qbmi-duration-row]');
      const status = root.querySelector('[data-qbmi-status-message]');
      const categoryElement = root.querySelector('[data-qbmi-category]');
      const marker = root.querySelector('[data-qbmi-marker]');
      const gauge = root.querySelector('.qbmi__gauge');
      const scoreDial = root.querySelector('[data-qbmi-score-dial]');
      const medicalWarning = root.querySelector('[data-qbmi-medical-warning]');
      const estimateNote = root.querySelector('[data-qbmi-estimate-note]');
      const programSelector = root.querySelector('.qbmi__program-selector');

      function showPrograms(maintenance) {
        root.querySelectorAll('[data-qbmi-program-option]').forEach((option) => {
          const duration = Number(option.dataset.qbmiProgramOption);
          option.hidden = maintenance ? duration !== 30 : duration === 30;
        });
        if (programSelector) programSelector.classList.toggle('is-maintenance', maintenance);
      }

      placeholder.hidden = true;
      result.hidden = false;
      setText('[data-qbmi-score]', formatDecimal(metrics.bmi));
      setText('[data-qbmi-category]', category.label);
      setText('[data-qbmi-healthy-range]', `${formatDecimal(metrics.healthyMinKg)}–${formatDecimal(metrics.healthyMaxKg)} kg`);

      if (categoryElement) categoryElement.dataset.category = category.id;
      if (marker) marker.style.setProperty('--qbmi-marker-position', `${metrics.gaugePosition}%`);
      if (scoreDial) scoreDial.style.setProperty('--qbmi-dial-position', `${metrics.gaugePosition}%`);
      if (gauge) gauge.setAttribute('aria-label', `Indice de masă corporală ${formatDecimal(metrics.bmi)}: ${category.label}`);
      if (medicalWarning) medicalWarning.hidden = true;
      if (estimateNote) estimateNote.hidden = false;

      const categoryMessages = {
        underweight: 'Greutatea este sub intervalul orientativ calculat.',
        normal: 'Greutatea se află în intervalul asociat unui IMC normal.',
        overweight: 'Greutatea este peste limita superioară a intervalului orientativ.',
        obesity: 'Rezultatul este peste limita superioară a intervalului orientativ.',
      };
      setText('[data-qbmi-category-copy]', categoryMessages[category.id] || 'Rezultat orientativ conform clasificării IMC pentru adulți.');

      if (category.id === 'underweight') {
        differenceLabel.textContent = 'Diferență până la limita inferioară a intervalului';
        setText('[data-qbmi-difference]', `${formatDecimal(metrics.kgBelowHealthyRange)} kg sub interval`);
        durationRow.hidden = true;
        status.className = 'qbmi__status-message is-caution';
        status.textContent = 'Calculatorul nu recomandă un program de slăbit pentru acest rezultat. Discută cu medicul sau cu un specialist în nutriție.';
        recommendedDuration = null;
        recommendation.hidden = true;
      } else if (category.id === 'normal') {
        differenceLabel.textContent = 'Poziția față de intervalul orientativ';
        setText('[data-qbmi-difference]', 'În interiorul intervalului');
        durationRow.hidden = true;
        status.className = 'qbmi__status-message is-positive';
        status.textContent = 'Greutatea introdusă se află în intervalul asociat unui IMC normal. Dacă dorești să îți menții silueta actuală, continuă cu obiceiuri echilibrate și poți descoperi Complexul Qualuca de 30 de zile.';
        recommendedDuration = metrics.recommendation.durationDays;
        recommendation.hidden = false;
        showPrograms(true);
        setText('[data-qbmi-recommendation-title]', 'Program pentru menținerea siluetei');
        setText('[data-qbmi-recommendation-copy]', 'Ești în intervalul normal. Îți afișăm Complexul Qualuca de 30 de zile ca opțiune pentru rutina de menținere.');
        setText('[data-qbmi-why-title]', 'De ce îți afișăm programul de 30 de zile?');
        if (estimateNote) estimateNote.hidden = true;
        renderExplanation(metrics);
        renderProduct(recommendedDuration);
      } else {
        differenceLabel.textContent = 'Diferență până la limita superioară a intervalului';
        setText('[data-qbmi-difference]', `${formatDecimal(metrics.kgAboveHealthyRange)} kg peste interval`);
        durationRow.hidden = false;
        const optimistic = core.displayDays(metrics.estimatedDays.optimistic);
        const conservative = core.displayDays(metrics.estimatedDays.conservative);
        setText('[data-qbmi-duration]', `aprox. ${optimistic}–${conservative} zile`);
        status.className = category.id === 'obesity' ? 'qbmi__status-message is-caution' : 'qbmi__status-message is-neutral';
        status.textContent = category.id === 'obesity'
          ? 'Acest rezultat este orientativ. Discută cu medicul înainte de începerea unui program de slăbit.'
          : 'Rezultatul este orientativ și poate fi folosit ca punct de plecare pentru obiective realiste.';

        recommendedDuration = metrics.recommendation.durationDays;
        recommendation.hidden = false;
        showPrograms(false);
        setText('[data-qbmi-recommendation-title]', 'Program recomandat pentru obiectivul tău');
        setText('[data-qbmi-recommendation-copy]', 'Complexul Qualuca propus de calculator este selectat automat. Poți compara și celelalte durate.');
        setText('[data-qbmi-why-title]', 'De ce îți recomandăm acest program?');
        renderExplanation(metrics);
        renderProduct(recommendedDuration);
        if (medicalWarning) medicalWarning.hidden = category.id !== 'obesity';
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      clearErrors();

      const selectedSex = sexInputs.find((input) => input.checked);
      const validation = core.validateInputs(weightInput.value, heightInput.value, true);
      if (!selectedSex) setError('sex', 'Alege Feminin sau Masculin pentru a continua.');
      if (!selectedSex || !validation.valid) {
        Object.entries(validation.errors).forEach(([field, message]) => setError(field, message));
        const firstInvalid = !selectedSex ? sexInputs[0] : form.querySelector('input[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      const calculationSignature = `${selectedSex.value}|${validation.values.weightKg}|${validation.values.heightCm}`;
      const submittedAt = Date.now();
      if (
        inFlightSignature === calculationSignature ||
        (lastSubmittedSignature === calculationSignature && submittedAt - lastSubmittedAt < 1500)
      ) return;

      lastMetrics = core.calculateMetrics(validation.values.weightKg, validation.values.heightCm);
      inFlightSignature = calculationSignature;
      lastSubmittedSignature = calculationSignature;
      lastSubmittedAt = submittedAt;
      eventToken = '';
      const currentSubmissionToken = createEventToken();
      submissionToken = currentSubmissionToken;
      identified = false;
      if (savePanel) savePanel.hidden = false;
      if (saveForm) saveForm.reset();
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.textContent = 'SALVEAZĂ REZULTATUL';
      }
      setSaveMessage('', false);
      renderMetrics(lastMetrics);
      track('bmi_calculator_used', privacyState);
      const currentPromise = persistCalculation(lastMetrics, currentSubmissionToken, selectedSex.value);
      calculationPromise = currentPromise;

      window.requestAnimationFrame(() => {
        resultCard.focus({preventScroll: true});
        if (window.matchMedia('(max-width: 767px)').matches) {
          resultCard.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
        }
      });

      try {
        const response = await currentPromise;
        if (submissionToken === currentSubmissionToken) {
          eventToken = response?.calculationEventId || currentSubmissionToken;
          if (response?.attributionToken) {
            privacyState.setAttribution(response.attributionToken);
            await syncCartAttribution(response.attributionToken, eventToken);
          }
        }
      } catch (_) {
        if (calculationPromise === currentPromise) {
          setSaveMessage('Rezultatul este afișat, dar salvarea statistică nu a putut fi confirmată. Poți recalcula.', false);
        }
      } finally {
        if (calculationPromise === currentPromise) inFlightSignature = '';
      }
    });

    [weightInput, heightInput].forEach((input) => {
      input.addEventListener('input', () => setError(input === weightInput ? 'weight' : 'height', ''));
    });
    sexInputs.forEach((input) => input.addEventListener('change', () => setError('sex', '')));
    sexField.addEventListener('keydown', (event) => {
      const currentIndex = sexInputs.indexOf(event.target);
      if (currentIndex === -1) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % sexInputs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + sexInputs.length) % sexInputs.length;
      if (event.key === ' ' || event.key === 'Enter') nextIndex = currentIndex;
      if (nextIndex === null) return;

      event.preventDefault();
      sexInputs[nextIndex].checked = true;
      sexInputs[nextIndex].focus();
      setError('sex', '');
    });
    root.querySelectorAll('[data-qbmi-program-option]').forEach((option) => {
      option.addEventListener('click', () => renderProduct(Number(option.dataset.qbmiProgramOption)));
    });

    if (saveForm) saveForm.addEventListener('submit', saveDetailedResult);

    const cta = root.querySelector('[data-qbmi-product-cta]');
    if (cta) {
      cta.addEventListener('click', async (event) => {
        if (cta.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        const destination = cta.href;
        await recordAttributedCta();
        track(`bmi_program_cta_clicked_${selectedDuration}`, privacyState);
        window.location.assign(destination);
      });
    }

    document.addEventListener('visitorConsentCollected', () => {
      const change = privacyState.refresh();
      if (change.withdrew && !identified) syncCartAttribution('', '').catch(() => {});
    });

    populateProgramPrices();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-qualuca-bmi]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
